import { Book, Clock, MessageSquare } from 'lucide-react'

import { ReadHistory } from '@/api/type'
import { DescriptionView } from '@/components/shared/description-view'
import { AuthImage } from '@/components/ui/auth-image'
import { Card, CardContent } from '@/components/ui/card'
import { formatTimeJST } from '@/lib/activity-format-utils'
import { formatTime, isDurationMime } from '@/lib/time-format'
import { stripQueryParams } from '@/lib/utils'

type Props = {
  item: ReadHistory
  onAddNote?: () => void
}

// ドメイン別のカラー
const DOMAIN_COLORS: Record<string, string> = {
  oreilly: 'bg-orange-500/20 text-orange-500 border-orange-500/30',
  tver: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
  fod: 'bg-red-500/20 text-red-500 border-red-500/30',
  line: 'bg-green-500/20 text-green-500 border-green-500/30',
  archive: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
  local: 'bg-gray-500/20 text-gray-500 border-gray-500/30',
  default: 'bg-muted text-muted-foreground border-muted',
}

// 所要時間を計算
const calculateDuration = (start: string, end: string): string => {
  const startDate = new Date(start)
  const endDate = new Date(end)
  const diffMs = endDate.getTime() - startDate.getTime()
  const diffMinutes = Math.round(diffMs / 60000)

  if (diffMinutes < 1) return '1分未満'
  if (diffMinutes < 60) return `${diffMinutes}分`

  const hours = Math.floor(diffMinutes / 60)
  const minutes = diffMinutes % 60
  return minutes > 0 ? `${hours}時間${minutes}分` : `${hours}時間`
}

// 進捗差を計算
const calculateProgress = (from: number, to: number): string => {
  const diff = to - from
  if (diff <= 0) return ''
  return `+${diff}`
}

export const ReadCard = ({ item, onAddNote }: Props) => {
  const colorClass = DOMAIN_COLORS[item.product_domain] ?? DOMAIN_COLORS.default
  const duration = calculateDuration(item.started_at, item.ended_at)
  const progress = calculateProgress(item.progress_from, item.progress_to)
  const isDuration = isDurationMime(item.mime_type)
  const progressFromLabel = isDuration ? formatTime(item.progress_from) : `p.${item.progress_from}`
  const progressToLabel = isDuration ? formatTime(item.progress_to) : `p.${item.progress_to}`
  const progressDiffLabel = progress
    ? isDuration
      ? `+${formatTime(item.progress_to - item.progress_from)}`
      : progress
    : ''
  // thumbnail_urlからクエリパラメータを除去してAuthImageに署名を任せる
  // (旧バックエンドの事前署名URL・新バックエンドの生パス両対応)
  const cleanPath = item.thumbnail_url ? stripQueryParams(item.thumbnail_url) : null
  const thumbnailSrc = cleanPath ? `/api${cleanPath}` : null
  const title =
    item.episode_title && item.episode_title !== item.product_title
      ? `${item.product_title} - ${item.episode_title}`
      : item.product_title

  return (
    <Card className={`p-0 border ${colorClass.split(' ').find((c) => c.startsWith('border-')) ?? ''}`}>
      <CardContent className="p-3">
        <div>
          <div className="flex items-start gap-3">
            {/* サムネイルまたはアイコン */}
            {thumbnailSrc ? (
              <AuthImage src={thumbnailSrc} alt={item.product_title} adaptive />
            ) : (
              <div className={`p-2 rounded-lg ${colorClass}`}>
                <Book className="size-5" />
              </div>
            )}

            {/* コンテンツ */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${colorClass}`}>
                  {item.product_domain.toUpperCase()}
                </span>
                <div className="text-xs text-muted-foreground">
                  {formatTimeJST(item.started_at)} - {formatTimeJST(item.ended_at)}
                </div>
              </div>

              <h3 className="font-medium text-sm truncate">{title}</h3>

              {item.episode_description && (
                <DescriptionView
                  description={item.episode_description}
                  className="mt-1 max-h-40 overflow-y-auto"
                  compact
                />
              )}

              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Clock className="size-3" />
                  {duration}
                </span>
              </div>
              {progress && (
                <span className="text-primary text-xs">
                  {progressFromLabel} → {progressToLabel} ({progressDiffLabel})
                </span>
              )}

              {/* メモボタン */}
              {onAddNote && (
                <button
                  type="button"
                  className="inline-flex items-center gap-1 mt-1 py-0.5 text-xs text-amber-600 hover:text-amber-700"
                  onClick={(e) => {
                    e.stopPropagation()
                    onAddNote()
                  }}
                >
                  <MessageSquare className="size-3" />
                  <span>メモ</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
